import moment from 'moment';
import * as Yup from 'yup';
import { getFormattedDateYmd, getMaskedDate } from '../../../global/helpers/date-helpers';
import { getRequiredText } from '../../../global/helpers/transformer';
import { YesNoIndicators } from '../../../global/models/api-model';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { ClergyBioChurchRelationship, RelationshipPerson } from '../model/clergy-bio-app-models';
import { ClergyBioRelationshipFormModel } from './model/form-models';

export function GetClergyBioRelationShipValidationSchema(settings: ClergyBioSettings, labelSetting: LabelSettingProps) {

	const childShape = Yup.object().shape({
		firstName: Yup.string().when(['lastName', 'gender', 'dob', 'middleName', 'lastNameAtBirth', 'suffix'], {
			is: (lastName, gender, dob, middleName, lastNameAtBirth, suffix) => lastName || gender || dob || middleName || lastNameAtBirth || suffix,
			then: Yup.string().required(getRequiredText(labelSetting.requiredFormat, settings.legalFirstNameLabel)),
		}),
		lastName: Yup.string().when(['firstName', 'gender', 'dob', 'middleName', 'lastNameAtBirth', 'suffix'], {
			is: (firstName, gender, dob, middleName, lastNameAtBirth, suffix) => firstName || gender || dob || middleName || lastNameAtBirth || suffix,
			then: Yup.string().required(getRequiredText(labelSetting.requiredFormat, settings.legalLastNameLabel)),
		}),
		gender: Yup.string().when(['firstName', 'lastName', 'dob', 'middleName', 'lastNameAtBirth', 'suffix'], {
			is: (firstName, lastName, dob, middleName, lastNameAtBirth, suffix) => firstName || lastName || dob || middleName || lastNameAtBirth || suffix,
			then: Yup.string().required(getRequiredText(labelSetting.requiredFormat, settings.genderLabel)),
		}),
		dob: Yup.string().nullable().when(['firstName', 'lastName', 'gender', 'middleName', 'lastNameAtBirth', 'suffix'], {
			is: (firstName, lastName, gender, middleName, lastNameAtBirth, suffix) => firstName || lastName || gender || middleName || lastNameAtBirth || suffix,
			then: Yup.string().nullable()
				.test('valid_format',
					(value, context) => {
						const dobMasked = value?.includes('XX/XX/');
						if (dobMasked) return true;

						if (!value) return context.createError({ message: getRequiredText(labelSetting.requiredFormat, labelSetting.dobLabel) });

						const valueMoment = moment(value);

						if (!valueMoment.isValid()) return context.createError({ message: 'Invalid Format' });

						const nowMoment = moment();

						return valueMoment.diff(nowMoment) >= 0 ? context.createError({ message: labelSetting.dateFutureError || 'Date cannot be in the future' }) : true;
					}),
		}),
	}, [['firstName', 'lastName'], ['firstName', 'gender'], ['firstName', 'dob'], ['lastName', 'dob'], ['lastName', 'gender'], ['dob', 'gender']]);

	return Yup.object().shape({
		parent_relations_attributes: Yup.array()
			.of(childShape),
		child_relations_attributes: Yup.array()
			.of(childShape),
	});
}

function transformPerson(person: RelationshipPerson, initialValue: RelationshipPerson) {
	return {
		first_name: person?.firstName,
		middle_name: person?.middleName,
		last_name: person?.lastName,
		gender: person?.gender,
		name_suffix: person?.suffix,
		date_of_birth: person?.dob?.toString()?.includes('XX/XX') ? initialValue.dob : getFormattedDateYmd(person?.dob),
		last_name_at_birth: person?.lastNameAtBirth,
	};
}

export function FormatRelationshipPerson(person: RelationshipPerson): RelationshipPerson {
	return {
		...person,
		dob: getMaskedDate(getFormattedDateYmd(person?.dob), 0),
		dom: getFormattedDateYmd(person?.dom),
	};
}

export function ConstructPayload(values: ClergyBioRelationshipFormModel, relationship: ClergyBioChurchRelationship) {

	const payload = {
		parent_relations_attributes: values?.parent_relations_attributes?.filter(p => p.firstName).map(a => { return { id: a?.id, parent_attributes: transformPerson(a, relationship?.parents?.details?.find(p => p?.id === a?.id)) }; }),
		child_relations_attributes: values?.child_relations_attributes?.map(a => { return { id: a?.id, stepchild: a?.stepchild, child_attributes: transformPerson(a, relationship?.children?.details?.find(p => p?.id === a?.id)) }; }),
	};

	return payload;
}

export function getNewRelationshipPerson(removable?: boolean) {

	return {

		firstName: '',
		middleName: '',
		lastName: '',
		lastNameAtBirth: '',
		gender: '',
		suffix: '',
		dob: null,
		dom: null,
		stepchild: YesNoIndicators.No,
		removable,
	};
}

function reportErrors(data: any[]): any {
	if (!data) return null;

	const errors = [];
	let hasErrors = false;

	data.forEach(
		itemDetail => {
			const innerItem = {};
			if (itemDetail?.errors) {
				const keys = Object.keys(itemDetail?.errors);
				for (let i = 0; i < keys.length; i += 1) {
					const keyName = keys[i];
					const value = itemDetail?.errors[keyName];
					if (value) {
						innerItem[keys[i]] = Array.isArray(value) ? value.join(', ') : value;
						hasErrors = true;
					}
				}
			}

			errors.push(innerItem);
		});

	return hasErrors ? errors : null;
}

export function findInnerErrors(data: any): any {
	if (!data || (data && (typeof data === 'string' || data instanceof String))) return null;

	const errors: any = {};

	const parentRelations = reportErrors(data?.parents?.details);
	if (parentRelations) {
		errors.parent_relations_attributes = parentRelations;
	}

	const childRelations = reportErrors(data?.children?.details);
	if (childRelations) {
		errors.child_relations_attributes = childRelations;
	}

	const currentSpouse = reportErrors(data?.currentSpouse?.details);
	if (currentSpouse) {
		errors.currentSpouse = currentSpouse;
	}

	const deceasedSpouse = reportErrors(data?.deceasedSpouse?.details);
	if (deceasedSpouse) {
		errors.deceasedSpouse = deceasedSpouse;
	}

	const previousSpouse = reportErrors(data?.previousSpouse?.details);
	if (previousSpouse) {
		errors.previousSpouse = previousSpouse;
	}

	return errors;
}